import { computed, ref } from 'vue'
import { getErrorMessage, logger, useKsI18n } from '@siclop/kstone-vue'
import { LeaveBalanceService } from '../services/LeaveBalanceService'
import type { LeaveAccrual } from '../models'

/** Acquisition mois par mois d'un solde (`GET /leave-balances/{id}/accruals`), en état local. */
export function useLeaveAccruals() {
  const { t } = useKsI18n()
  const accruals = ref<LeaveAccrual[]>([])
  const loading = ref(false)
  const error = ref('')
  let sequence = 0

  /** Lignes triées par mois ; les ajustements d'un même mois gardent l'ordre de l'API. */
  const sorted = computed(() => [...accruals.value].sort((a, b) => (a.month ?? '').localeCompare(b.month ?? '')))

  /** Total des jours acquis (somme des lignes). */
  const total = computed(() => accruals.value.reduce((sum, a) => sum + (a.accruedDays ?? 0), 0))

  async function load(balanceId: number): Promise<void> {
    const id = ++sequence
    loading.value = true
    error.value = ''
    accruals.value = []
    try {
      const result = await LeaveBalanceService.accruals(balanceId)
      if (id === sequence) accruals.value = result ?? []
    } catch (e: unknown) {
      if (id !== sequence) return
      error.value = getErrorMessage(e, t('ksproject.hr.leaveAccrual.loadError', 'Erreur lors du chargement de l\'acquisition'))
      logger.error('Chargement de l\'acquisition du solde', e)
    } finally {
      if (id === sequence) loading.value = false
    }
  }

  return { accruals: sorted, total, loading, error, load }
}
